import { useEffect } from 'react';
import { Link } from '@tanstack/react-router';
import { Edit3 } from 'lucide-react';
import {
  DashboardDataTable,
  DashboardDataTableColumnKind,
  DashboardTableSearchInput,
  buildDashboardDataTableColumns,
  dashboardTableTextActionClassName,
  DashboardTableRowActionItem,
  DashboardTableRowActions,
} from '@shigo-ui/dashboard-ui';

import { useDebouncedValue } from '../../../../../hooks/useDebouncedValue';
import { dashboardT } from '../../../../../lib/messages';
import { useShigoPermissions } from '../../../../auth/useShigoPermissions';
import { canEditLists } from '../../access';
import { useAllListsQuery } from '../../hooks';
import {
  listsPageSizes,
  parseRewriteRules,
  SupplyListsTab,
  SupplyListType,
  type RewriteList,
  type SchainList,
} from '../../model';
import { useListsRouteFilters } from '../../useListsRouteFilters';

interface AllListRow {
  id: string;
  name: string;
  type: SupplyListType;
  items: number | null;
  tab: SupplyListsTab.Rewrite | SupplyListsTab.Schain;
}

export function AllListsPage() {
  const { can } = useShigoPermissions();
  const canEdit = canEditLists(can);
  const routeFilters = useListsRouteFilters(SupplyListsTab.All);
  const search = useDebouncedValue(
    routeFilters.filter.searchTerm,
    (value) => {
      routeFilters.setSearch(value);
    },
    650,
  );
  const query = useAllListsQuery(routeFilters.filter);
  const rows = [
    ...(query.data?.rewrite ?? []).map((item) => toRewriteRow(item)),
    ...(query.data?.schain ?? []).map((item) => toSchainRow(item)),
  ];
  const totalItems = query.data?.totalElements ?? 0;
  const pageCount = Math.max(1, Math.ceil(totalItems / routeFilters.filter.size));
  const safePage = Math.min(routeFilters.filter.page, pageCount - 1);
  useEffect(() => {
    if (!query.isLoading && safePage !== routeFilters.filter.page) {
      routeFilters.setPagination(safePage, routeFilters.filter.size);
    }
  }, [query.isLoading, routeFilters, safePage]);
  const columns = buildDashboardDataTableColumns<AllListRow>([
    {
      id: 'name',
      kind: DashboardDataTableColumnKind.Text,
      header: dashboardT('lists.name'),
      headerClassName: 'w-[55%]',
      className: 'w-[55%] font-medium',
      value: (item) =>
        canEdit ? (
          <Link
            className={dashboardTableTextActionClassName()}
            to={`/supply/lists/${item.tab}/edit/$listId`}
            params={{ listId: item.id }}
          >
            {item.name}
          </Link>
        ) : (
          item.name
        ),
    },
    {
      id: 'type',
      kind: DashboardDataTableColumnKind.Text,
      header: dashboardT('lists.type'),
      headerClassName: 'w-40',
      className: 'w-40',
      value: (item) =>
        item.type === SupplyListType.Rewrite
          ? dashboardT('lists.tabs.rewrite')
          : dashboardT('lists.tabs.schain'),
    },
    {
      id: 'items',
      kind: DashboardDataTableColumnKind.Text,
      header: dashboardT('lists.items'),
      headerClassName: 'w-32',
      className: 'w-32',
      value: (item) => item.items ?? '-',
    },
    {
      id: 'actions',
      kind: DashboardDataTableColumnKind.Actions,
      hidden: !canEdit,
      cell: (item) => (
        <DashboardTableRowActions label={dashboardT('lists.actions')}>
          <DashboardTableRowActionItem asChild>
            <Link to={`/supply/lists/${item.tab}/edit/$listId`} params={{ listId: item.id }}>
              <Edit3 className="h-4 w-4" aria-hidden="true" />
              {dashboardT('lists.edit')}
            </Link>
          </DashboardTableRowActionItem>
        </DashboardTableRowActions>
      ),
    },
  ]);
  return (
    <main className="bg-background page-gutter min-h-0 flex-1 overflow-x-hidden py-4 md:py-6">
      <DashboardDataTable
        title={dashboardT('lists.allTitle')}
        columns={columns}
        items={rows}
        getRowKey={(item) => `${item.type}-${item.id}`}
        loading={query.isLoading}
        errorMessage={query.isError ? dashboardT('lists.loadError') : undefined}
        emptyMessage={dashboardT('lists.empty')}
        loadingMessage={dashboardT('lists.loading')}
        minWidthClassName="min-w-[640px]"
        filterToolbar={
          query.data?.partialFailure ? (
            <p role="status" className="text-warning-foreground text-sm">
              {dashboardT('lists.partialLoadError')}
            </p>
          ) : undefined
        }
        toolbar={
          <DashboardTableSearchInput
            aria-label={dashboardT('lists.search')}
            value={search.draft}
            onChange={(event) => {
              search.setDraft(event.target.value);
            }}
            placeholder={dashboardT('lists.search')}
          />
        }
        pagination={{
          value: { page: safePage, pageSize: routeFilters.filter.size, totalItems },
          pageSizes: listsPageSizes,
          disabled: query.isLoading,
          onChange: ({ page, pageSize }) => {
            routeFilters.setPagination(page, pageSize);
          },
        }}
      />
    </main>
  );
}

function toRewriteRow(item: RewriteList): AllListRow {
  const rules = parseRewriteRules(item.rules);
  return {
    id: item.id,
    name: item.name,
    type: SupplyListType.Rewrite,
    items: rules.success ? rules.data.length : null,
    tab: SupplyListsTab.Rewrite,
  };
}

function toSchainRow(item: SchainList): AllListRow {
  return {
    id: item.id,
    name: item.name,
    type: SupplyListType.Schain,
    items: item.schainBundleList.length,
    tab: SupplyListsTab.Schain,
  };
}
